import { useEffect, useState } from "react";

import { Slider } from "@/app/components/ui/slider";

interface ProficiencyRangeProps {
	setLevel: (level: string) => void;
	value: string;
}

/**
 * Proficiency slider. Keeps the legacy value scale: a 0-60 track with step 10,
 * where the stored levels snap to 10, 20, 30, 40, and 50.
 */
function ProficiencyRange({ setLevel, value }: ProficiencyRangeProps) {
	const [proficiency, setValue] = useState(10);

	const onValueChange = (values: number[]) => {
		const nextValue = values[0];

		if (nextValue <= 10) {
			setLevel("Elementary Proficiency");
			return setValue(10);
		}
		if (nextValue > 10 && nextValue <= 20) {
			setLevel("Limited Working Proficiency");
			return setValue(20);
		}
		if (nextValue > 20 && nextValue <= 30) {
			setLevel("Professional Working Proficiency");
			return setValue(30);
		}
		if (nextValue > 30 && nextValue <= 40) {
			setLevel("Full Professional Proficiency");
			return setValue(40);
		}
		if (nextValue >= 50) {
			setLevel("Native / Bilingual Proficiency");
			return setValue(50);
		}
	};

	useEffect(() => {
		// The track paints a thumb at Elementary from the very first render, so a
		// reader who is happy with that level never moves it and never fires
		// `onValueChange`. The stored proficiency stayed empty while the control
		// showed a choice, and the save refused with "Pick a language and a
		// proficiency" pointing at a field that looked filled in. Adopt what is
		// already on screen instead of letting the two disagree.
		if (!value) {
			setValue(10);
			setLevel("Elementary Proficiency");
			return;
		}

		if (value === "Elementary Proficiency") setValue(10);
		// The legacy slider wrote "Limited Working Proficiency" but only read the
		// lowercase variant back; accept both so a saved level restores the thumb.
		if (value === "Limited working Proficiency" || value === "Limited Working Proficiency") setValue(20);
		if (value === "Professional Working Proficiency") setValue(30);
		if (value === "Full Professional Proficiency") setValue(40);
		if (value === "Native / Bilingual Proficiency") setValue(50);
	}, [value]);

	return (
		<div>
			<div className="relative py-2">
				<Slider min={0} max={60} step={10} value={[proficiency]} onValueChange={onValueChange} />
				<div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-[8.5%]">
					{new Array(5).fill(0).map((_, index) => (
						<div
							className={`size-2 rounded-full ${proficiency === (index + 1) * 10 ? "bg-transparent" : "border border-gray-300 bg-white"}`}
							key={index}
						/>
					))}
				</div>
			</div>
			<div className="mt-1 flex justify-between text-center text-gray-600 text-xs">
				<span>
					Elementary
					<br />
					Proficiency
				</span>
				<span>
					Limited
					<br />
					working
				</span>
				<span>
					Professional
					<br />
					working
				</span>
				<span>
					Full
					<br />
					Professional
				</span>
				<span>
					Native/
					<br />
					Bilingual
				</span>
			</div>
		</div>
	);
}

export default ProficiencyRange;
